import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { PROJECTS, PROJECT_CATEGORIES } from "../data/site";
import { Reveal } from "./Reveal";
import { useLang } from "../i18n/LanguageContext";

export default function PortfolioGrid({ preview = false }) {
  const [filter, setFilter] = useState("Tutti");
  const navigate = useNavigate();
  const { t } = useLang();

  const visible = preview
    ? PROJECTS.slice(0, 4)
    : filter === "Tutti"
      ? PROJECTS
      : PROJECTS.filter((p) => p.group === filter);

  return (
    <div>
      {!preview && (
        <Reveal delay={0.1} className="flex flex-wrap gap-2">
          {PROJECT_CATEGORIES.map((cat) => (
            <button
              key={cat}
              onClick={() => setFilter(cat)}
              data-testid={`portfolio-filter-${cat.toLowerCase()}`}
              className={`rounded-full border px-4 py-2 text-xs font-semibold uppercase tracking-wider transition-colors duration-300 ${
                filter === cat
                  ? "border-brand bg-brand text-white"
                  : "border-line bg-white text-slate-500 hover:border-brand/60 hover:text-brand"
              }`}
            >
              {t.filters[cat]}
            </button>
          ))}
        </Reveal>
      )}

      <motion.div
        layout
        className={`mt-10 grid grid-cols-1 gap-5 ${preview ? "sm:grid-cols-2 lg:grid-cols-4" : "sm:grid-cols-2 lg:grid-cols-3"}`}
        data-testid="portfolio-grid"
      >
        <AnimatePresence mode="popLayout">
          {visible.map((project, i) => {
            const tr = t.projects[project.id];
            return (
              <motion.button
                layout
                key={project.id}
                initial={{ opacity: 0, scale: 0.94 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.94 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                onClick={() => navigate(`/lavori/${project.id}`)}
                data-testid={`project-card-${project.id}`}
                className={`group relative overflow-hidden rounded-2xl border border-line bg-white text-left shadow-sm ${
                  !preview && filter === "Tutti" && i === 0 ? "sm:col-span-2 sm:row-span-2" : ""
                }`}
              >
                <img
                  src={project.image}
                  alt={tr.title}
                  loading="lazy"
                  className={`w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105 ${
                    !preview && filter === "Tutti" && i === 0 ? "aspect-[4/3] sm:h-full sm:aspect-auto" : "aspect-[4/3]"
                  }`}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/15 to-transparent opacity-80 transition-opacity duration-500 group-hover:opacity-100" />
                <div className="absolute inset-x-0 bottom-0 p-5">
                  <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-orange-400">{tr.category}</span>
                  <h3 className="mt-1 font-heading text-lg font-semibold text-white">{tr.title}</h3>
                </div>
                <span className="absolute right-4 top-4 flex h-9 w-9 translate-y-2 items-center justify-center rounded-full bg-brand text-white opacity-0 transition-[opacity,transform] duration-300 group-hover:translate-y-0 group-hover:opacity-100">
                  <ArrowUpRight size={17} />
                </span>
              </motion.button>
            );
          })}
        </AnimatePresence>
      </motion.div>

      {preview && (
        <Reveal delay={0.2} className="mt-10 text-center">
          <Link
            to="/lavori"
            data-testid="portfolio-view-all"
            className="group inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-7 py-3.5 text-sm font-semibold text-slate-800 transition-colors duration-300 hover:border-brand hover:text-brand"
          >
            {t.projectsPage.viewAll}
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      )}
    </div>
  );
}
